import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { backendUrl } from '@/lib/bubbleImage';
import { useAuth } from '@/lib/auth';
import { Lighthouse, MapPin, Clock, Users, Sparkle, Heart, Crosshair, ArrowLeft } from '@phosphor-icons/react';
import LiveSky from '@/components/LiveSky';

const ConvergencePostcard = ({ postcard }) => (
  <div className="relative grad-border p-0 overflow-hidden" data-testid="convergence-postcard">
    {postcard.video_url ? (
      <div className="aspect-[16/9] relative">
        <video src={backendUrl(postcard.video_url)}
                poster={postcard.portrait_url ? backendUrl(postcard.portrait_url) : undefined}
                autoPlay loop muted playsInline
                className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
      </div>
    ) : postcard.portrait_url ? (
      <div className="aspect-[16/9] relative">
        <img src={backendUrl(postcard.portrait_url)} alt="convergence postcard"
              className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />
      </div>
    ) : (
      <div className="aspect-[16/9] bg-gradient-to-br from-bx-cyan/30 to-bx-magenta/30" />
    )}
    <div className="absolute inset-0 p-5 sm:p-7 flex flex-col justify-end">
      <div className="text-[10px] uppercase tracking-[0.4em] text-bx-gold flex items-center gap-1">
        <Sparkle size={11} weight="fill" /> convergence postcard · auto-minted
      </div>
      <div className="font-display text-lg sm:text-xl italic text-white/95 mt-2 max-w-2xl leading-snug">
        {postcard.flourish}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/55 mt-2">
        {(postcard.bubble_names || []).slice(0, 6).join(' · ')}
      </div>
    </div>
  </div>
);

const LighthouseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [lh, setLh] = useState(null);
  const [bubbles, setBubbles] = useState([]);
  const [pickedBubble, setPickedBubble] = useState('');
  const [mode, setMode] = useState('in_person');
  const [amount, setAmount] = useState(25);
  const [anon, setAnon] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/lighthouses/${id}`),
        api.get('/bubbles'),
      ]);
      setLh(a.data.lighthouse);
      setBubbles(b.data.bubbles || []);
      if (b.data.bubbles?.[0]) setPickedBubble(b.data.bubbles[0].id);
    } catch (err) { toast.error('could not load'); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const rsvp = async () => {
    if (!pickedBubble) { toast.error('pick a bubble first'); return; }
    setBusy('rsvp');
    try {
      await api.post(`/lighthouses/${id}/rsvp`, { bubble_id: pickedBubble, mode });
      toast.success(mode === 'remote' ? 'your bubble will lean in from afar' : 'see you there');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not rsvp'); }
    finally { setBusy(null); }
  };

  const contribute = async () => {
    setBusy('pool');
    try {
      await api.post(`/lighthouses/${id}/pool/contribute`, {
        amount_resonance: Number(amount),
        anonymous: anon,
      });
      toast.success(`${amount} ✦ dropped in the pool`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'could not contribute'); }
    finally { setBusy(null); }
  };

  const scan = async () => {
    if (!pickedBubble) { toast.error('pick a bubble first'); return; }
    if (!navigator.geolocation) { toast.error('location not supported on this device'); return; }
    setBusy('scan');
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/lighthouses/${id}/scan`, {
          bubble_id: pickedBubble,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        if (data.payout_resonance > 0) {
          toast.success(`you arrived · +${data.payout_resonance} ✦ from the pool`);
        } else if (data.already_scanned) {
          toast.message('your bubble is already here');
        } else {
          toast.success(`you arrived · position #${data.scan_position}`);
        }
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'could not scan');
      } finally { setBusy(null); }
    }, () => {
      toast.error('we need your location to verify you arrived');
      setBusy(null);
    }, { enableHighAccuracy: true, timeout: 15_000 });
  };

  if (!lh) return <div className="text-center py-20 text-white/65">opening the lighthouse…</div>;

  const photoUrl = lh.place_photo_ref
    ? `${api.defaults.baseURL}/places/photo?ref=${encodeURIComponent(lh.place_photo_ref)}&max_width=1200`
    : null;
  const when = new Date(lh.scheduled_at);
  const isHost = user?.id === lh.host_user_id;
  const totalScans = (lh.scans || []).length;
  const totalRsvps = (lh.rsvps || []).length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 pb-32" data-testid="lighthouse-detail">
      <Link to="/lighthouses" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/55 hover:text-white mb-4">
        <ArrowLeft size={12} weight="bold" /> all lighthouses
      </Link>

      <div className="relative rounded-3xl overflow-hidden">
        {photoUrl ? (
          <div className="aspect-[16/8] relative">
            <img src={photoUrl} alt={lh.place_name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
          </div>
        ) : (
          <div className="aspect-[16/8] bg-gradient-to-br from-bx-cyan/20 to-bx-magenta/20" />
        )}
        <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end">
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan flex items-center gap-2 mb-1">
            <Lighthouse size={12} weight="fill" /> {lh.kind}
            {lh.is_adult && <span className="px-2 py-0.5 rounded-full bg-bx-magenta/30 text-bx-magenta text-[9px] ml-2">21+</span>}
            {lh.status === 'converged' && <span className="px-2 py-0.5 rounded-full bg-bx-volt/30 text-bx-volt text-[9px] ml-2">live</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-black leading-tight">{lh.place_name}</h1>
          <div className="text-white/65 text-sm mt-1 flex items-center gap-1"><MapPin size={12} weight="duotone" /> {lh.place_address}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
        <div className="lg:col-span-2 space-y-5">
          {/* LIVE SKY · only inside the window and when window opens (15min pre → 30min post) */}
          {(() => {
            try {
              const sched = new Date(lh.scheduled_at);
              const start = new Date(sched.getTime() - 15 * 60 * 1000);
              const end = new Date(sched.getTime() + ((lh.window_minutes || 120) + 30) * 60 * 1000);
              const now = new Date();
              if (pickedBubble && now >= start && now <= end && lh.status !== 'closed') {
                return <LiveSky lighthouse={lh} bubbleId={pickedBubble} />;
              }
            } catch { /* ignore */ }
            return null;
          })()}
          {lh.postcard && (
            <ConvergencePostcard postcard={lh.postcard} />
          )}
          {lh.blurb && (
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-2">the host says</div>
              <div className="text-white/85 italic">"{lh.blurb}"</div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-white/60">
                — @{lh.host_username} · carried by {lh.host_bubble_name}
              </div>
            </div>
          )}

          <div className="glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-bx-volt mb-3">when</div>
            <div className="flex items-center gap-2 text-xl font-display font-bold">
              <Clock size={20} weight="duotone" className="text-bx-volt" />
              {when.toLocaleString([], { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </div>
            <div className="text-xs text-white/55 mt-1">window · {lh.window_minutes} minutes</div>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-bx-magenta mb-3">who's drifting in</div>
            {totalRsvps === 0 ? (
              <div className="text-white/45 text-sm italic">no rsvps yet · be the first</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(lh.rsvps || []).map((r) => (
                  <div key={`${r.user_id}-${r.ts}`}
                    className="px-3 py-1.5 rounded-full glass-hi text-xs flex items-center gap-1.5">
                    <Heart size={11} weight="fill" className={r.mode === 'in_person' ? 'text-bx-volt' : 'text-bx-cyan'} />
                    <span className="font-bold">{r.bubble_name}</span>
                    <span className="text-white/60">·</span>
                    <span className="text-[10px] uppercase tracking-widest text-white/55">{r.mode === 'remote' ? 'from afar' : 'in person'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(lh.scans || []).length > 0 && (
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-bx-volt mb-3">arrived</div>
              <div className="flex flex-wrap gap-2">
                {(lh.scans || []).map((s, i) => (
                  <div key={`${s.user_id}-${s.ts}`}
                    className="px-3 py-1.5 rounded-full bg-bx-volt/15 text-bx-volt text-xs flex items-center gap-1.5 font-bold">
                    #{i + 1} · {s.bubble_name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {/* Pool */}
          <div className="glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-bx-gold mb-2">gift pool</div>
            <div className="font-display text-3xl font-black text-bx-volt">{lh.pool_resonance || 0} ✦</div>
            <div className="text-xs text-white/55 mt-1">
              splits to the first {lh.pool_first_n} bubbles that physically arrive
            </div>
            {!isHost && lh.status === 'open' && (
              <div className="mt-4 space-y-2">
                <div className="flex gap-2">
                  {[10, 25, 100].map((n) => (
                    <button key={n} onClick={() => setAmount(n)}
                      data-testid={`pool-quick-${n}`}
                      className={`flex-1 px-2 py-1.5 rounded-full text-xs font-bold ${
                        Number(amount) === n ? 'bg-bx-volt text-black' : 'glass-hi hover:bg-white/10'
                      }`}>
                      {n}✦
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-[11px] text-white/65">
                  <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
                  drop anonymously
                </label>
                <button onClick={contribute} disabled={busy === 'pool'}
                  data-testid="pool-contribute-btn"
                  className="w-full btn-volt px-3 py-2 rounded-full text-xs font-bold disabled:opacity-50">
                  {busy === 'pool' ? 'dropping…' : `drop ${amount} ✦ in the pool`}
                </button>
              </div>
            )}
          </div>

          {/* RSVP / Scan */}
          <div className="glass rounded-2xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-2">your bubble</div>
            <select
              value={pickedBubble}
              onChange={(e) => setPickedBubble(e.target.value)}
              data-testid="lh-bubble-select"
              className="w-full glass-hi rounded-xl p-2 text-sm bg-transparent outline-none">
              {bubbles.map((b) => <option key={b.id} value={b.id} className="bg-black">{b.name}</option>)}
            </select>

            <div className="mt-3 flex gap-2">
              <button onClick={() => setMode('in_person')}
                data-testid="mode-in-person"
                className={`flex-1 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${mode==='in_person' ? 'bg-bx-volt text-black' : 'glass-hi'}`}>
                in person
              </button>
              <button onClick={() => setMode('remote')}
                data-testid="mode-remote"
                className={`flex-1 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${mode==='remote' ? 'bg-bx-cyan text-black' : 'glass-hi'}`}>
                from afar
              </button>
            </div>

            <button onClick={rsvp} disabled={busy === 'rsvp'}
              data-testid="lh-rsvp-btn"
              className="mt-3 w-full px-3 py-2 rounded-full glass-hi text-xs font-bold hover:bg-white/10 disabled:opacity-50">
              {busy === 'rsvp' ? 'rsvping…' : "i'll be there"}
            </button>

            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">when you arrive · in the window</div>
              <button onClick={scan} disabled={busy === 'scan'}
                data-testid="lh-scan-btn"
                className="w-full btn-volt px-3 py-2 rounded-full text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
                <Crosshair size={14} weight="bold" /> {busy === 'scan' ? 'locating…' : "i'm here — claim ✦"}
              </button>
              <div className="text-[10px] text-white/60 mt-2 italic">
                we'll check you're within 250m of the venue. your exact location is never stored.
              </div>
            </div>
          </div>

          {lh.pool_contributors && lh.pool_contributors.length > 0 && (
            <div className="glass rounded-2xl p-5">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-3">contributions</div>
              {lh.pool_contributors.slice(-8).reverse().map((c, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-b-0">
                  <span className="text-white/65">{c.anonymous ? 'someone kind' : `@${c.username}`}</span>
                  <span className="text-bx-volt font-bold">+{c.amount} ✦</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LighthouseDetail;
